// src/components/Admin/AdminReportsTable.jsx
import React, { useState } from 'react';
import { updateReportStatus } from '../../firebase';
import EnhancedConclusionModal from './EnhancedConclusionModal';
import './AdminReportsTable.css';

const AdminReportsTable = ({ reports, onStatusChange }) => {
  const [updatingId, setUpdatingId] = useState(null);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [localReports, setLocalReports] = useState(reports || []);

  // Aggiorna lo stato locale quando cambiano i reports dal padre
  React.useEffect(() => {
    setLocalReports(reports || []);
  }, [reports]);

  // Calcola i counter per le segnalazioni
  const counters = React.useMemo(() => {
    const inAttesa = localReports.filter(r => r.status === 'In attesa').length;
    const presaInCarico = localReports.filter(r => r.status === 'Presa in carico').length;
    const concluse = localReports.filter(r => r.status === 'Conclusa').length;
    const totale = localReports.length;

    return { inAttesa, presaInCarico, concluse, totale };
  }, [localReports]);

  // Funzione per formattare la data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    let date;
    
    if (dateString && typeof dateString.toDate === 'function') {
      date = dateString.toDate();
    } else if (dateString instanceof Date) {
      date = dateString;
    } else if (typeof dateString === 'string') {
      if (dateString.includes('/')) {
        return dateString;
      }
      
      const parts = dateString.split('-');
      if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
      }
      
      date = new Date(dateString);
    } else {
      date = new Date(dateString);
    }
    
    if (date && !isNaN(date.getTime())) {
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `${day}/${month}/${year}`;
    }
    
    return String(dateString);
  };

  // Funzione per determinare la classe di colore in base allo stato
  const getStatusClass = (status) => {
    switch (status) {
      case "Conclusa":
        return "status-conclusa";
      case "Presa in carico":
        return "status-presa-carico";
      case "In attesa":
        return "status-in-attesa";
      default:
        return "";
    }
  };

  // Funzione per ottenere l'icona dello stato
  const getStatusIcon = (status) => {
    switch (status) {
      case "Conclusa":
        return "✓";
      case "Presa in carico":
        return "⚡";
      case "In attesa":
        return "⏳";
      default:
        return "";
    }
  };
  
  // Gestisce il cambio di stato di una segnalazione con controllo per note
  const handleStatusChange = async (reportId, targetStatus) => {
    if (updatingId) return;
    
    // Se il nuovo stato è "Conclusa", mostra il modal potenziato
    if (targetStatus === "Conclusa") {
      const report = localReports.find(r => r.id === reportId);
      setSelectedReport(report);
      setNewStatus(targetStatus);
      setAdminNotes('');
      setShowNotesForm(true);
      return;
    }
    
    // Per altri stati, procedi direttamente
    await processStatusUpdate(reportId, targetStatus, '');
  };

  // Processa l'aggiornamento dello stato
  const processStatusUpdate = async (reportId, status, notes = '') => {
    setUpdatingId(reportId);
    console.log(`Tentativo di aggiornare stato del report ${reportId} a ${status} con note: "${notes}"`);
    
    try {
      await updateReportStatusWithNotes(reportId, status, notes);
      
      // Aggiorna lo stato locale immediatamente per feedback visivo
      setLocalReports(prev => prev.map(report => 
        report.id === reportId 
          ? { 
              ...report, 
              status: status, 
              adminNotes: notes || report.adminNotes,
              adminNotesDate: notes ? new Date() : report.adminNotesDate,
              lastUpdate: new Date()
            }
          : report
      ));
      
      // Chiama la funzione del componente padre se esiste
      if (onStatusChange && typeof onStatusChange === 'function') {
        try {
          await onStatusChange(reportId, status, notes);
        } catch (error) {
          console.warn('Errore nella callback onStatusChange:', error);
        }
      }
      
      console.log("Stato e note aggiornati con successo");
      
    } catch (error) {
      console.error("Errore nell'aggiornamento dello stato:", error);
      alert("Si è verificato un errore durante l'aggiornamento dello stato. Riprova.");
    } finally {
      setUpdatingId(null);
    }
  };

  // Funzione per aggiornare lo stato del report con note
  const updateReportStatusWithNotes = async (reportId, status, notes) => {
    try {
      console.log(`updateReportStatusWithNotes: Aggiornamento reportId=${reportId} a ${status} con note="${notes}"`);
      
      if (!reportId) throw new Error("reportId è obbligatorio");
      if (!status) throw new Error("status è obbligatorio");
      
      await updateReportStatus(reportId, status, notes);
      
      console.log(`updateReportStatusWithNotes: Stato aggiornato con successo a ${status}`);
      return true;
    } catch (error) {
      console.error(`updateReportStatusWithNotes: Errore nell'aggiornamento dello stato:`, error);
      throw error;
    }
  };

  // Gestisce la chiusura del modal
  const handleModalClose = () => {
    setShowNotesForm(false);
    setSelectedReport(null);
    setAdminNotes('');
    setNewStatus('');
  };

  // Gestisce l'aggiornamento da parte del modal
  const handleReportUpdate = (reportId, status, notes) => {
    // Aggiorna lo stato locale
    setLocalReports(prev => prev.map(report => 
      report.id === reportId 
        ? { 
            ...report, 
            status: status, 
            adminNotes: notes || report.adminNotes,
            adminNotesDate: notes ? new Date() : report.adminNotesDate,
            lastUpdate: new Date()
          }
        : report
    ));

    // Chiama la funzione del componente padre se esiste
    if (onStatusChange && typeof onStatusChange === 'function') {
      try {
        onStatusChange(reportId, status, notes);
      } catch (error) {
        console.warn('Errore nella callback onStatusChange:', error);
      }
    }
  };

  return (
    <>
      {/* Counter Cards */}
      <div className="reports-counter-container">
        <div className="counter-card counter-total">
          <div className="counter-icon">📊</div>
          <div className="counter-content">
            <div className="counter-number">{counters.totale}</div>
            <div className="counter-label">Totale Segnalazioni</div>
          </div>
        </div>

        <div className="counter-card counter-in-attesa">
          <div className="counter-icon">⏳</div>
          <div className="counter-content">
            <div className="counter-number">{counters.inAttesa}</div>
            <div className="counter-label">In Attesa</div>
          </div>
        </div>

        <div className="counter-card counter-presa-carico">
          <div className="counter-icon">⚡</div>
          <div className="counter-content">
            <div className="counter-number">{counters.presaInCarico}</div>
            <div className="counter-label">Prese in Carico</div>
          </div>
        </div>

        <div className="counter-card counter-concluse">
          <div className="counter-icon">✓</div>
          <div className="counter-content">
            <div className="counter-number">{counters.concluse}</div>
            <div className="counter-label">Concluse</div>
          </div>
        </div>
      </div>

      {/* Tabella Segnalazioni */}
      <div className="table-responsive reports-table-container">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Data</th>
              <th>Utente</th>
              <th>Descrizione</th>
              <th>Stato</th>
              <th>Note Admin</th>
              <th>Ultima modifica</th>
              <th>Azioni</th>
            </tr>
          </thead>
          <tbody>
          {!localReports || localReports.length === 0 ? (
              <tr>
                <td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}>
                  <div className="no-reports-message">
                    <div className="no-reports-icon">📝</div>
                    <div>Nessuna segnalazione trovata</div>
                  </div>
                </td>
              </tr>
            ) : (
              localReports.map((report) => (
                <tr key={report.id} className={`report-row ${getStatusClass(report.status)}`}>
                  <td>{formatDate(report.date)}</td>
                  <td>
                    <div className="user-info">
                      <div className="user-name">{report.userName || report.userEmail}</div>
                    </div>
                  </td>
                  <td>
                    <div className="description-cell">
                      {report.description}
                    </div>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(report.status)}`}>
                      <span className="status-icon">{getStatusIcon(report.status)}</span>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    {report.adminNotes ? (
                      <div className="admin-notes-cell">
                        <div className="admin-notes-text">
                          {report.adminNotes}
                        </div>
                        {report.adminNotesDate && (
                          <div className="admin-notes-date">
                            {formatDate(report.adminNotesDate)}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span className="no-notes">-</span>
                    )}
                  </td>
                  <td>{formatDate(report.lastUpdate)}</td>
                  <td>
                    <div className="action-buttons-container">
                      {report.status === 'In attesa' && (
                        <>
                          <button
                            className="action-btn btn-presa-carico"
                            onClick={() => handleStatusChange(report.id, 'Presa in carico')}
                            disabled={updatingId === report.id}
                            title="Prendi in carico"
                          >
                            <span className="btn-icon">⚡</span>
                            <span className="btn-text">Prendi in carico</span>
                          </button>
                          <button
                            className="action-btn btn-conclusa"
                            onClick={() => handleStatusChange(report.id, 'Conclusa')}
                            disabled={updatingId === report.id}
                            title="Chiudi segnalazione"
                          >
                            <span className="btn-icon">✓</span>
                            <span className="btn-text">Concludi</span>
                          </button>
                        </>
                      )}
                      
                      {report.status === 'Presa in carico' && (
                        <>
                          <button
                            className="action-btn btn-in-attesa"
                            onClick={() => handleStatusChange(report.id, 'In attesa')}
                            disabled={updatingId === report.id}
                            title="Rimetti in attesa"
                          >
                            <span className="btn-icon">⏳</span>
                            <span className="btn-text">In attesa</span>
                          </button>
                          <button
                            className="action-btn btn-conclusa"
                            onClick={() => handleStatusChange(report.id, 'Conclusa')}
                            disabled={updatingId === report.id}
                            title="Chiudi segnalazione"
                          >
                            <span className="btn-icon">✓</span>
                            <span className="btn-text">Concludi</span>
                          </button>
                        </>
                      )}

                      {report.status === 'Conclusa' && (
                        <>
                          <button
                            className="action-btn btn-presa-carico"
                            onClick={() => handleStatusChange(report.id, 'Presa in carico')}
                            disabled={updatingId === report.id}
                            title="Riapri segnalazione"
                          >
                            <span className="btn-icon">⚡</span>
                            <span className="btn-text">Riapri</span>
                          </button>
                        </>
                      )}

                      {updatingId === report.id && (
                        <div className="updating-spinner">
                          <div className="spinner-icon">⟳</div>
                          Aggiornamento...
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal per conclusione con note */}
      <EnhancedConclusionModal
        selectedReport={selectedReport}
        onClose={handleModalClose}
        onReportUpdate={handleReportUpdate}
        showNotesForm={showNotesForm}
        updatingId={updatingId}
      />
    </>
  );
};

export default AdminReportsTable;